import { useState, useEffect, useCallback } from 'react'
import { Card } from '@trafiflow/shared-ui'
import {
  FileText,
  History,
  PlayCircle,
  Activity,
  ChevronRight,
  AlertCircle,
  Loader2,
  Check,
  Clock,
  Sparkles,
  Save,
  RotateCcw,
  ChevronDown,
  ChevronUp,
  List,
  Network,
} from 'lucide-react'
import { promptEngineeringService } from '@/services/prompt-engineering'
import type {
  PromptResponse,
  PromptCategory,
  TestPromptResponse,
  ExecutionResponse,
} from '@/services/prompt-engineering'
import { AgentArchitectureGraph, AgentNodeModal } from '@/components/pipeline-graph'
import type { AgentDefinition } from '@/components/pipeline-graph'

type TabType = 'editor' | 'history' | 'playground' | 'executions'
type ViewMode = 'list' | 'graph'
type PipelineFilter = 'onboarding' | 'storyboard' | 'all'

export function PromptEngineeringPage() {
  // State
  const [prompts, setPrompts] = useState<PromptResponse[]>([])
  const [selectedPrompt, setSelectedPrompt] = useState<PromptResponse | null>(null)
  const [versions, setVersions] = useState<PromptResponse[]>([])
  const [activeTab, setActiveTab] = useState<TabType>('editor')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [categoryFilter, setCategoryFilter] = useState<PromptCategory | 'all'>('all')
  const [viewMode, setViewMode] = useState<ViewMode>('list')
  const [pipelineFilter, setPipelineFilter] = useState<PipelineFilter>('onboarding')
  const [selectedAgent, setSelectedAgent] = useState<AgentDefinition | null>(null)

  // Editor state
  const [systemPrompt, setSystemPrompt] = useState('')
  const [userPromptTemplate, setUserPromptTemplate] = useState('')
  const [model, setModel] = useState('')
  const [description, setDescription] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)

  // Playground state
  const [variables, setVariables] = useState<Record<string, string>>({})
  const [testResult, setTestResult] = useState<TestPromptResponse | null>(null)
  const [isTesting, setIsTesting] = useState(false)

  // Executions state
  const [executions, setExecutions] = useState<ExecutionResponse[]>([])
  const [loadingExecutions, setLoadingExecutions] = useState(false)
  const [expandedExecution, setExpandedExecution] = useState<string | null>(null)

  // Load prompts
  const fetchPrompts = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await promptEngineeringService.listPrompts()
      setPrompts(response.prompts || [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load prompts')
    } finally {
      setLoading(false)
    }
  }, [])

  // Load versions when prompt is selected
  const fetchVersions = useCallback(async (promptId: string) => {
    try {
      const response = await promptEngineeringService.getPromptVersions(promptId)
      setVersions(response.versions || [])
    } catch (err) {
      console.error('Failed to load versions:', err)
    }
  }, [])

  // Load executions for selected prompt
  const fetchExecutions = useCallback(async (promptId: string) => {
    try {
      setLoadingExecutions(true)
      const response = await promptEngineeringService.listPromptExecutions(promptId, 50)
      setExecutions(response.executions || [])
    } catch (err) {
      console.error('Failed to load executions:', err)
    } finally {
      setLoadingExecutions(false)
    }
  }, [])

  useEffect(() => {
    fetchPrompts()
  }, [fetchPrompts])

  // When prompt is selected, load its data
  useEffect(() => {
    if (selectedPrompt) {
      setSystemPrompt(selectedPrompt.system_prompt)
      setUserPromptTemplate(selectedPrompt.user_prompt_template)
      setModel(selectedPrompt.model)
      setDescription(selectedPrompt.description)
      setHasChanges(false)

      // Initialize variables
      const vars: Record<string, string> = {}
      selectedPrompt.variables.forEach((v) => {
        vars[v] = ''
      })
      setVariables(vars)

      // Load versions and executions
      fetchVersions(selectedPrompt.prompt_id)
      fetchExecutions(selectedPrompt.prompt_id)
    }
  }, [selectedPrompt, fetchVersions, fetchExecutions])

  // Track changes
  useEffect(() => {
    if (selectedPrompt) {
      const changed =
        systemPrompt !== selectedPrompt.system_prompt ||
        userPromptTemplate !== selectedPrompt.user_prompt_template ||
        model !== selectedPrompt.model ||
        description !== selectedPrompt.description
      setHasChanges(changed)
    }
  }, [systemPrompt, userPromptTemplate, model, description, selectedPrompt])

  // Save new version
  const handleSaveVersion = async () => {
    if (!selectedPrompt || !hasChanges) return

    try {
      setIsSaving(true)
      const newVersion = await promptEngineeringService.createPromptVersion(
        selectedPrompt.prompt_id,
        {
          system_prompt: systemPrompt,
          user_prompt_template: userPromptTemplate,
          model,
          description,
        }
      )
      setSelectedPrompt(newVersion)
      setHasChanges(false)
      await fetchVersions(selectedPrompt.prompt_id)
      await fetchPrompts()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save version')
    } finally {
      setIsSaving(false)
    }
  }

  // Activate version
  const handleActivateVersion = async (version: number) => {
    if (!selectedPrompt) return

    try {
      await promptEngineeringService.activatePromptVersion(selectedPrompt.prompt_id, version)
      await fetchVersions(selectedPrompt.prompt_id)
      await fetchPrompts()
      // Update selected prompt to the activated version
      const activatedVersion = versions.find((v) => v.version === version)
      if (activatedVersion) {
        setSelectedPrompt({ ...activatedVersion, is_active: true })
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to activate version')
    }
  }

  // Test prompt
  const handleTestPrompt = async () => {
    if (!selectedPrompt) return

    try {
      setIsTesting(true)
      setTestResult(null)
      const result = await promptEngineeringService.testPrompt(selectedPrompt.prompt_id, {
        variables,
      })
      setTestResult(result)
    } catch (err) {
      setTestResult({
        system_prompt: '',
        user_prompt: '',
        raw_output: '',
        duration_ms: 0,
        status: 'failed',
        error: err instanceof Error ? err.message : 'Test failed',
      })
    } finally {
      setIsTesting(false)
    }
  }

  // Filter prompts by category
  const filteredPrompts = prompts.filter(
    (p) => categoryFilter === 'all' || p.category === categoryFilter
  )

  // Group prompts by category for display
  const onboardingPrompts = filteredPrompts.filter((p) => p.category === 'onboarding')
  const storyboardPrompts = filteredPrompts.filter((p) => p.category === 'storyboard')
  const swiperPrompts = filteredPrompts.filter((p) => p.category === 'swiper')
  const assistantPrompts = filteredPrompts.filter((p) => p.category === 'assistant')

  return (
    <div className="flex h-full">
      {/* Sidebar - Prompt List */}
      <div className="w-80 border-r border-gray-200 bg-gray-50 flex flex-col">
        <div className="p-4 border-b border-gray-200 space-y-3">
          <h2 className="text-lg font-semibold text-gray-900">Prompts</h2>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value as PromptCategory | 'all')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="all">All Categories</option>
            <option value="onboarding">Onboarding</option>
            <option value="storyboard">Storyboard</option>
            <option value="swiper">Swiper</option>
            <option value="assistant">Assistant</option>
          </select>

          {/* View Toggle */}
          <div className="flex items-center rounded-lg border border-gray-300 overflow-hidden">
            <button
              onClick={() => setViewMode('list')}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium transition-colors ${
                viewMode === 'list'
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <List className="w-3.5 h-3.5" />
              List
            </button>
            <button
              onClick={() => {
                setViewMode('graph')
                setSelectedPrompt(null) // Clear selected prompt when switching to graph
              }}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium transition-colors ${
                viewMode === 'graph'
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Network className="w-3.5 h-3.5" />
              Graph
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : error ? (
            <div className="flex items-center gap-2 text-red-600 text-sm">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          ) : (
            <>
              {(categoryFilter === 'all' || categoryFilter === 'onboarding') &&
                onboardingPrompts.length > 0 && (
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">
                      Onboarding
                    </h3>
                    <div className="space-y-1">
                      {onboardingPrompts.map((prompt) => (
                        <PromptListItem
                          key={prompt.prompt_id}
                          prompt={prompt}
                          isSelected={selectedPrompt?.prompt_id === prompt.prompt_id}
                          onClick={() => setSelectedPrompt(prompt)}
                        />
                      ))}
                    </div>
                  </div>
                )}

              {(categoryFilter === 'all' || categoryFilter === 'storyboard') &&
                storyboardPrompts.length > 0 && (
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">
                      Storyboard
                    </h3>
                    <div className="space-y-1">
                      {storyboardPrompts.map((prompt) => (
                        <PromptListItem
                          key={prompt.prompt_id}
                          prompt={prompt}
                          isSelected={selectedPrompt?.prompt_id === prompt.prompt_id}
                          onClick={() => setSelectedPrompt(prompt)}
                        />
                      ))}
                    </div>
                  </div>
                )}

              {(categoryFilter === 'all' || categoryFilter === 'swiper') &&
                swiperPrompts.length > 0 && (
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">
                      Swiper
                    </h3>
                    <div className="space-y-1">
                      {swiperPrompts.map((prompt) => (
                        <PromptListItem
                          key={prompt.prompt_id}
                          prompt={prompt}
                          isSelected={selectedPrompt?.prompt_id === prompt.prompt_id}
                          onClick={() => setSelectedPrompt(prompt)}
                        />
                      ))}
                    </div>
                  </div>
                )}

              {(categoryFilter === 'all' || categoryFilter === 'assistant') &&
                assistantPrompts.length > 0 && (
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">
                      Assistant
                    </h3>
                    <div className="space-y-1">
                      {assistantPrompts.map((prompt) => (
                        <PromptListItem
                          key={prompt.prompt_id}
                          prompt={prompt}
                          isSelected={selectedPrompt?.prompt_id === prompt.prompt_id}
                          onClick={() => setSelectedPrompt(prompt)}
                        />
                      ))}
                    </div>
                  </div>
                )}

              {filteredPrompts.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-4">No prompts found</p>
              )}
            </>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {selectedPrompt ? (
          <>
            {/* Header */}
            <div className="p-4 border-b border-gray-200 bg-white">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">{selectedPrompt.name}</h1>
                  <p className="text-sm text-gray-500">{selectedPrompt.prompt_id}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                    v{selectedPrompt.version}
                  </span>
                  {selectedPrompt.is_active && (
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                      Active
                    </span>
                  )}
                </div>
              </div>

              {/* Tabs */}
              <div className="flex gap-1 mt-4">
                <TabButton
                  active={activeTab === 'editor'}
                  onClick={() => setActiveTab('editor')}
                  icon={<FileText className="w-4 h-4" />}
                  label="Editor"
                />
                <TabButton
                  active={activeTab === 'history'}
                  onClick={() => setActiveTab('history')}
                  icon={<History className="w-4 h-4" />}
                  label="History"
                />
                <TabButton
                  active={activeTab === 'playground'}
                  onClick={() => setActiveTab('playground')}
                  icon={<PlayCircle className="w-4 h-4" />}
                  label="Playground"
                />
                <TabButton
                  active={activeTab === 'executions'}
                  onClick={() => setActiveTab('executions')}
                  icon={<Activity className="w-4 h-4" />}
                  label="Executions"
                />
              </div>
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
              {activeTab === 'editor' && (
                <EditorTab
                  systemPrompt={systemPrompt}
                  setSystemPrompt={setSystemPrompt}
                  userPromptTemplate={userPromptTemplate}
                  setUserPromptTemplate={setUserPromptTemplate}
                  model={model}
                  setModel={setModel}
                  description={description}
                  setDescription={setDescription}
                  variables={selectedPrompt.variables}
                  hasChanges={hasChanges}
                  isSaving={isSaving}
                  onSave={handleSaveVersion}
                  onReset={() => {
                    setSystemPrompt(selectedPrompt.system_prompt)
                    setUserPromptTemplate(selectedPrompt.user_prompt_template)
                    setModel(selectedPrompt.model)
                    setDescription(selectedPrompt.description)
                  }}
                />
              )}

              {activeTab === 'history' && (
                <HistoryTab
                  versions={versions}
                  currentVersion={selectedPrompt.version}
                  onActivate={handleActivateVersion}
                  onSelect={(v) => {
                    setSystemPrompt(v.system_prompt)
                    setUserPromptTemplate(v.user_prompt_template)
                    setModel(v.model)
                    setDescription(v.description)
                  }}
                />
              )}

              {activeTab === 'playground' && (
                <PlaygroundTab
                  variables={variables}
                  setVariables={setVariables}
                  expectedVariables={selectedPrompt.variables}
                  testResult={testResult}
                  isTesting={isTesting}
                  onTest={handleTestPrompt}
                />
              )}

              {activeTab === 'executions' && (
                <ExecutionsTab
                  executions={executions}
                  loading={loadingExecutions}
                  expandedExecution={expandedExecution}
                  setExpandedExecution={setExpandedExecution}
                />
              )}
            </div>
          </>
        ) : viewMode === 'graph' ? (
          /* Graph View - Show Agent Architecture */
          <div className="flex-1 flex flex-col bg-white">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">Agent Architecture</h1>
                  <p className="text-sm text-gray-500 mt-1">
                    Click on an agent to view and edit its prompts
                  </p>
                </div>
                {/* Pipeline Selector */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-500">Pipeline:</span>
                  <select
                    value={pipelineFilter}
                    onChange={(e) => setPipelineFilter(e.target.value as PipelineFilter)}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white"
                  >
                    <option value="onboarding">Ad Generator (Onboarding)</option>
                    <option value="storyboard">Storyboard Creation</option>
                    <option value="all">All Agents</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="flex-1">
              <AgentArchitectureGraph
                highlightedPromptId={undefined}
                pipelineFilter={pipelineFilter}
                onAgentSelect={(agent) => {
                  setSelectedAgent(agent)
                }}
                className="h-full"
              />
            </div>

            {/* Agent Node Modal */}
            {selectedAgent && (
              <AgentNodeModal
                agent={selectedAgent}
                prompts={prompts}
                isOpen={!!selectedAgent}
                onClose={() => setSelectedAgent(null)}
                onSave={() => {
                  fetchPrompts()
                }}
              />
            )}
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-gray-50">
            <div className="text-center text-gray-500">
              <Sparkles className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium">Select a prompt to begin</p>
              <p className="text-sm">Choose from the list on the left</p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ──────────────────────────────────────────────────────────────────────────────
// Sub-components
// ──────────────────────────────────────────────────────────────────────────────

function PromptListItem({
  prompt,
  isSelected,
  onClick,
}: {
  prompt: PromptResponse
  isSelected: boolean
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-3 py-2 rounded-lg transition-colors flex items-center gap-2 ${
        isSelected ? 'bg-blue-100 text-blue-900' : 'hover:bg-gray-100 text-gray-700'
      }`}
    >
      <FileText className="w-4 h-4 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{prompt.name}</p>
        <p className="text-xs text-gray-500 truncate">v{prompt.version}</p>
      </div>
      <ChevronRight className="w-4 h-4 text-gray-400" />
    </button>
  )
}

function TabButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean
  onClick: () => void
  icon: React.ReactNode
  label: string
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
        active
          ? 'bg-blue-100 text-blue-700'
          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
      }`}
    >
      {icon}
      {label}
    </button>
  )
}

function EditorTab({
  systemPrompt,
  setSystemPrompt,
  userPromptTemplate,
  setUserPromptTemplate,
  model,
  setModel,
  description,
  setDescription,
  variables,
  hasChanges,
  isSaving,
  onSave,
  onReset,
}: {
  systemPrompt: string
  setSystemPrompt: (v: string) => void
  userPromptTemplate: string
  setUserPromptTemplate: (v: string) => void
  model: string
  setModel: (v: string) => void
  description: string
  setDescription: (v: string) => void
  variables: string[]
  hasChanges: boolean
  isSaving: boolean
  onSave: () => void
  onReset: () => void
}) {
  return (
    <div className="space-y-6">
      {/* Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {hasChanges && (
            <span className="text-sm text-amber-600 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              Unsaved changes
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onReset}
            disabled={!hasChanges}
            className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50 flex items-center gap-1"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
          <button
            onClick={onSave}
            disabled={!hasChanges || isSaving}
            className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save New Version
          </button>
        </div>
      </div>

      {/* Model & Description */}
      <Card className="p-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <optgroup label="Gemini 3 Series (Latest)">
                <option value="gemini-3.1-pro-preview">gemini-3.1-pro-preview</option>
                <option value="gemini-3-flash-preview">gemini-3-flash-preview</option>
                <option value="gemini-3.1-flash-lite-preview">gemini-3.1-flash-lite-preview</option>
                <option value="gemini-3.1-flash-image-preview">gemini-3.1-flash-image-preview</option>
                <option value="gemini-3-pro-image-preview">gemini-3-pro-image-preview</option>
              </optgroup>
              <optgroup label="Gemini 2.5 Pro">
                <option value="gemini-2.5-pro">gemini-2.5-pro</option>
                <option value="gemini-2.5-pro-preview-tts">gemini-2.5-pro-preview-tts</option>
              </optgroup>
              <optgroup label="Gemini 2.5 Flash">
                <option value="gemini-2.5-flash">gemini-2.5-flash</option>
                <option value="gemini-2.5-flash-lite">gemini-2.5-flash-lite</option>
                <option value="gemini-2.5-flash-image">gemini-2.5-flash-image</option>
                <option value="gemini-2.5-flash-native-audio-preview-12-2025">gemini-2.5-flash-native-audio</option>
                <option value="gemini-2.5-flash-preview-tts">gemini-2.5-flash-preview-tts</option>
              </optgroup>
              <optgroup label="Gemini 2.0">
                <option value="gemini-2.0-flash">gemini-2.0-flash</option>
                <option value="gemini-2.0-flash-lite">gemini-2.0-flash-lite</option>
              </optgroup>
              <optgroup label="Gemini 1.5">
                <option value="gemini-1.5-pro">gemini-1.5-pro</option>
                <option value="gemini-1.5-flash">gemini-1.5-flash</option>
                <option value="gemini-1.5-flash-8b">gemini-1.5-flash-8b</option>
              </optgroup>
              <optgroup label="Embeddings">
                <option value="gemini-embedding-2-preview">gemini-embedding-2-preview</option>
                <option value="gemini-embedding-001">gemini-embedding-001</option>
              </optgroup>
              <optgroup label="Specialized">
                <option value="gemini-robotics-er-1.5-preview">gemini-robotics-er-1.5-preview</option>
                <option value="veo-3.1-generate-preview">veo-3.1-generate-preview</option>
                <option value="lyria-realtime-exp">lyria-realtime-exp</option>
              </optgroup>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              placeholder="Brief description of this version..."
            />
          </div>
        </div>
      </Card>

      {/* Variables */}
      <Card className="p-4">
        <h3 className="text-sm font-medium text-gray-700 mb-2">Expected Variables</h3>
        <div className="flex flex-wrap gap-2">
          {variables.map((v) => (
            <code
              key={v}
              className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded font-mono"
            >
              {`{{${v}}}`}
            </code>
          ))}
        </div>
      </Card>

      {/* System Prompt */}
      <Card className="p-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">System Prompt</label>
        <textarea
          value={systemPrompt}
          onChange={(e) => setSystemPrompt(e.target.value)}
          rows={12}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono resize-y"
          placeholder="System prompt..."
        />
      </Card>

      {/* User Prompt Template */}
      <Card className="p-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          User Prompt Template
        </label>
        <textarea
          value={userPromptTemplate}
          onChange={(e) => setUserPromptTemplate(e.target.value)}
          rows={12}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono resize-y"
          placeholder="User prompt template with {{variables}}..."
        />
      </Card>
    </div>
  )
}

function HistoryTab({
  versions,
  currentVersion,
  onActivate,
  onSelect,
}: {
  versions: PromptResponse[]
  currentVersion: number
  onActivate: (version: number) => void
  onSelect: (version: PromptResponse) => void
}) {
  return (
    <div className="space-y-2">
      {versions.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-8">No version history</p>
      ) : (
        versions.map((v) => (
          <Card
            key={v.version}
            className={`p-4 cursor-pointer hover:bg-gray-50 ${
              v.version === currentVersion ? 'ring-2 ring-blue-500' : ''
            }`}
            onClick={() => onSelect(v)}
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">Version {v.version}</span>
                  {v.is_active && (
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                      Active
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500 mt-1">{v.description || 'No description'}</p>
                <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {new Date(v.created_at).toLocaleString()}
                  </span>
                  <span>by {v.created_by}</span>
                </div>
              </div>
              {!v.is_active && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onActivate(v.version)
                  }}
                  className="px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg"
                >
                  Activate
                </button>
              )}
            </div>
          </Card>
        ))
      )}
    </div>
  )
}

function PlaygroundTab({
  variables,
  setVariables,
  expectedVariables,
  testResult,
  isTesting,
  onTest,
}: {
  variables: Record<string, string>
  setVariables: (v: Record<string, string>) => void
  expectedVariables: string[]
  testResult: TestPromptResponse | null
  isTesting: boolean
  onTest: () => void
}) {
  return (
    <div className="space-y-6">
      {/* Variables Input */}
      <Card className="p-4">
        <h3 className="text-sm font-medium text-gray-700 mb-4">Test Variables</h3>
        <div className="space-y-3">
          {expectedVariables.map((varName) => (
            <div key={varName}>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {`{{${varName}}}`}
              </label>
              <textarea
                value={variables[varName] || ''}
                onChange={(e) => setVariables({ ...variables, [varName]: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono resize-y"
                placeholder={`Value for ${varName}...`}
              />
            </div>
          ))}
        </div>
        <button
          onClick={onTest}
          disabled={isTesting}
          className="mt-4 w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {isTesting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Running...
            </>
          ) : (
            <>
              <PlayCircle className="w-4 h-4" />
              Run Test
            </>
          )}
        </button>
      </Card>

      {/* Test Result */}
      {testResult && (
        <Card className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-700">Result</h3>
            <div className="flex items-center gap-2">
              <span
                className={`px-2 py-0.5 text-xs rounded-full ${
                  testResult.status === 'success'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                }`}
              >
                {testResult.status}
              </span>
              <span className="text-xs text-gray-500">{testResult.duration_ms}ms</span>
            </div>
          </div>

          {testResult.error ? (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {testResult.error}
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <h4 className="text-xs font-medium text-gray-500 mb-1">Rendered System Prompt</h4>
                <pre className="p-3 bg-gray-100 rounded-lg text-xs font-mono overflow-x-auto whitespace-pre-wrap">
                  {testResult.system_prompt}
                </pre>
              </div>
              <div>
                <h4 className="text-xs font-medium text-gray-500 mb-1">Rendered User Prompt</h4>
                <pre className="p-3 bg-gray-100 rounded-lg text-xs font-mono overflow-x-auto whitespace-pre-wrap">
                  {testResult.user_prompt}
                </pre>
              </div>
              <div>
                <h4 className="text-xs font-medium text-gray-500 mb-1">Raw Output</h4>
                <pre className="p-3 bg-gray-900 text-green-400 rounded-lg text-xs font-mono overflow-x-auto whitespace-pre-wrap max-h-96">
                  {testResult.raw_output}
                </pre>
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  )
}

function ExecutionsTab({
  executions,
  loading,
  expandedExecution,
  setExpandedExecution,
}: {
  executions: ExecutionResponse[]
  loading: boolean
  expandedExecution: string | null
  setExpandedExecution: (id: string | null) => void
}) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    )
  }

  if (executions.length === 0) {
    return <p className="text-sm text-gray-500 text-center py-8">No executions found</p>
  }

  return (
    <div className="space-y-2">
      {executions.map((exec) => (
        <Card key={exec.execution_id} className="overflow-hidden">
          <button
            onClick={() =>
              setExpandedExecution(expandedExecution === exec.execution_id ? null : exec.execution_id)
            }
            className="w-full p-4 text-left hover:bg-gray-50"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {exec.status === 'success' ? (
                  <Check className="w-4 h-4 text-green-600" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-red-600" />
                )}
                <div>
                  <div className="flex items-center gap-2">
                    <span className={`px-1.5 py-0.5 text-xs rounded ${
                      exec.source === 'playground'
                        ? 'bg-gray-100 text-gray-600'
                        : exec.source === 'storyboard'
                        ? 'bg-purple-100 text-purple-700'
                        : exec.source === 'ad_generator'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {exec.source || 'playground'}
                    </span>
                    <span className="font-medium text-sm">
                      {exec.job_type} / {exec.stage}
                    </span>
                    <span className="text-xs text-gray-400">v{exec.prompt_version}</span>
                  </div>
                  <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                    <span>{new Date(exec.created_at).toLocaleString()}</span>
                    <span>{exec.duration_ms}ms</span>
                    <span>{exec.model}</span>
                  </div>
                </div>
              </div>
              {expandedExecution === exec.execution_id ? (
                <ChevronUp className="w-4 h-4 text-gray-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-400" />
              )}
            </div>
          </button>

          {expandedExecution === exec.execution_id && (
            <div className="border-t border-gray-200 p-4 bg-gray-50 space-y-4">
              {exec.error_message && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {exec.error_message}
                </div>
              )}

              <div>
                <h4 className="text-xs font-medium text-gray-500 mb-1">Input Variables</h4>
                <pre className="p-3 bg-gray-100 rounded-lg text-xs font-mono overflow-x-auto">
                  {JSON.stringify(exec.input_variables, null, 2)}
                </pre>
              </div>

              <div>
                <h4 className="text-xs font-medium text-gray-500 mb-1">System Prompt Sent</h4>
                <pre className="p-3 bg-gray-100 rounded-lg text-xs font-mono overflow-x-auto whitespace-pre-wrap max-h-48">
                  {exec.system_prompt_sent}
                </pre>
              </div>

              <div>
                <h4 className="text-xs font-medium text-gray-500 mb-1">User Prompt Sent</h4>
                <pre className="p-3 bg-gray-100 rounded-lg text-xs font-mono overflow-x-auto whitespace-pre-wrap max-h-48">
                  {exec.user_prompt_sent}
                </pre>
              </div>

              <div>
                <h4 className="text-xs font-medium text-gray-500 mb-1">Raw Output</h4>
                <pre className="p-3 bg-gray-900 text-green-400 rounded-lg text-xs font-mono overflow-x-auto whitespace-pre-wrap max-h-96">
                  {exec.raw_output}
                </pre>
              </div>
            </div>
          )}
        </Card>
      ))}
    </div>
  )
}
